/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _merge from 'lodash/merge';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FocusScope } from 'react-aria';
import { Cross, Hamburger } from '@manulife/mux-cds-icons';
import { Manulife, ManulifeFr } from '@manulife/mux-logos';
import PropTypes from 'prop-types';
import { SVG_ICON_SIZE_DEFAULT } from '../Icon/constants.js';
import i18n from '../../lang/i18n/index.js';
import LANG from '../../lang/lang.js';
import { BREAKPOINTS } from '../../spacing/index.js';
import Element from '../../utils/Element.js';
import { rem, noop } from '../../utils/functions.js';
import { getDefaultTab } from '../../utils/getDefaultTab.js';
import handle from '../../utils/handle.js';
import BrandLogo from './BrandLogo.js';
import LogoComponent from './LogoComponent.js';
import MainNav from './MainNav.js';
import SideNavContext from './SideNavContext.js';
import { Root, MobileNavButton } from './styledComponents.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';
import useLang from '../../hooks/useLang.js';
import useToggle from '../../hooks/useToggle.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import useMedia from '../../hooks/useMedia.js';

var SideNav = function SideNav(_ref) {
  var _mobileLogo$props;
  var controlledId = _ref.id,
    _ref$isWideMainNav = _ref.isWideMainNav,
    isWideMainNav = _ref$isWideMainNav === undefined ? false : _ref$isWideMainNav,
    _ref$keepOpenOnSelect = _ref.keepOpenOnSelect,
    keepOpenOnSelect = _ref$keepOpenOnSelect === undefined ? true : _ref$keepOpenOnSelect,
    _ref$shouldCloseOnBlu = _ref.shouldCloseOnBlur,
    shouldCloseOnBlur = _ref$shouldCloseOnBlu === undefined ? true : _ref$shouldCloseOnBlu,
    _ref$onToggleSideNav = _ref.onToggleSideNav,
    onToggleSideNav = _ref$onToggleSideNav === undefined ? noop : _ref$onToggleSideNav,
    _ref$brandLogoProps = _ref.brandLogoProps,
    brandLogoProps = _ref$brandLogoProps === undefined ? {
      as: undefined,
      alt: undefined,
      href: undefined,
      onClick: undefined
    } : _ref$brandLogoProps,
    _ref$wordmarkLogoProp = _ref.wordmarkLogoProps,
    wordmarkLogoProps = _ref$wordmarkLogoProp === undefined ? {
      as: undefined,
      alt: undefined,
      href: undefined,
      onClick: undefined
    } : _ref$wordmarkLogoProp,
    logo = _ref.logo,
    mobileLogo = _ref.mobileLogo,
    skipLinks = _ref.skipLinks,
    defaultActiveKey = _ref.activeKey,
    _ref$mobileTabs = _ref.mobileTabs,
    mobileTabs = _ref$mobileTabs === undefined ? [] : _ref$mobileTabs,
    _ref$onChangeMobileTa = _ref.onChangeMobileTabs,
    onChangeMobileTabs = _ref$onChangeMobileTa === undefined ? noop : _ref$onChangeMobileTa,
    mobileSelectedTab = _ref.mobileSelectedTab,
    _ref$onToggleMobileNa = _ref.onToggleMobileNav,
    onToggleMobileNav = _ref$onToggleMobileNa === undefined ? noop : _ref$onToggleMobileNa,
    _ref$mobileHeaderItem = _ref.mobileHeaderItems,
    mobileHeaderItems = _ref$mobileHeaderItem === undefined ? [] : _ref$mobileHeaderItem,
    _ref$mobileFooterItem = _ref.mobileFooterItems,
    mobileFooterItems = _ref$mobileFooterItem === undefined ? [] : _ref$mobileFooterItem,
    _ref$children = _ref.children,
    children = _ref$children === undefined ? null : _ref$children,
    mainNavRef = _ref.mainNavRef,
    subNavRef = _ref.subNavRef;
  var _useDerivedState = useDerivedState(useId(), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var lang = useLang();
  var utilityHeaderHeight = Number.parseInt(theme.sideNav.mainNav.top, 10);
  var _useState = useState(utilityHeaderHeight === 0),
    _useState2 = _slicedToArray(_useState, 2),
    isSticky = _useState2[0],
    setSticky = _useState2[1];
  var _useState3 = useState(defaultActiveKey),
    _useState4 = _slicedToArray(_useState3, 2),
    activeKey = _useState4[0],
    setActiveKey = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    activeParentKey = _useState6[0],
    setActiveParentKey = _useState6[1];
  var _useState7 = useState(),
    _useState8 = _slicedToArray(_useState7, 2),
    expandedParentKey = _useState8[0],
    setExpandedParentKey = _useState8[1];
  var _useToggle = useToggle(false),
    _useToggle2 = _slicedToArray(_useToggle, 3),
    isMobileNavExpanded = _useToggle2[0],
    toggleMobileNav = _useToggle2[1],
    setMobileNavExpanded = _useToggle2[2];
  var _useState9 = useState(getDefaultTab(mobileTabs, mobileSelectedTab)),
    _useState10 = _slicedToArray(_useState9, 2),
    selectedTab = _useState10[0],
    setSelectedTab = _useState10[1];
  var isSideNavExpanded = !!expandedParentKey;
  useEffect(function () {
    onToggleSideNav(isSideNavExpanded);
  }, [onToggleSideNav, isSideNavExpanded]);
  useEffect(function () {
    var handleScroll = function handleScroll() {
      return utilityHeaderHeight && window.scrollY <= utilityHeaderHeight ? setSticky(false) : setSticky(true);
    };
    window.addEventListener('scroll', handleScroll);
    return function () {
      return window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only run on mount and unmount

  var outsideClickTarget = useRef();
  useOnClickOutside(outsideClickTarget, function () {
    return shouldCloseOnBlur && setExpandedParentKey();
  });
  var showHamburgerMenu = useMedia(["(max-width: ".concat(BREAKPOINTS.MAX_1024, "px)")],
  // media queries
  [true],
  // values corresponding to media query
  false // default value
  );
  var wordmarkLogoAs = wordmarkLogoProps.as,
    wordmarkLogoHref = wordmarkLogoProps.href,
    wordmarkLogoOnClick = wordmarkLogoProps.onClick,
    wordmarkLogoAlt = wordmarkLogoProps.alt;

  // mobile nav logos and button props
  var mobileProps = useMemo(function () {
    var ToggleIcon = isMobileNavExpanded ? Cross : Hamburger; // NOSONAR
    var toggleLabel = isMobileNavExpanded ? i18n[lang].sideNav.hamburgerCloseLabel : i18n[lang].sideNav.hamburgerOpenLabel;
    var toggleAriaLabel = isMobileNavExpanded ? i18n[lang].sideNav.hamburgerClose : i18n[lang].sideNav.hamburgerOpen;
    var logoProps = {
      brand: {
        height: '60px',
        width: '60px'
      },
      wordmark: {
        height: theme.sideNav.mobile.wordmarkLogo.height,
        paddingLeft: theme.sideNav.mobile.wordmarkLogo.paddingLeft,
        paddingRight: theme.sideNav.mobile.wordmarkLogo.paddingRight,
        maxWidth: theme.sideNav.mobile.wordmarkLogo.maxWidth
      }
    };
    return {
      toggle: {
        icon: /*#__PURE__*/jsx(ToggleIcon, {
          fontSize: rem(SVG_ICON_SIZE_DEFAULT)
        }),
        label: toggleLabel,
        ariaLabel: toggleAriaLabel
      },
      logo: logoProps
    };
  }, [isMobileNavExpanded, lang, theme]);
  var logoStyle = _merge({}, {
    // Set defaults from theme
    height: mobileProps.logo.wordmark.height,
    maxWidth: mobileProps.logo.wordmark.maxWidth,
    paddingLeft: mobileProps.logo.wordmark.paddingLeft,
    paddingRight: mobileProps.logo.wordmark.paddingRight,
    pointerEvents: !wordmarkLogoHref && !wordmarkLogoOnClick ? 'none' : undefined
  }, mobileLogo === null || mobileLogo === undefined || (_mobileLogo$props = mobileLogo.props) === null || _mobileLogo$props === undefined ? undefined : _mobileLogo$props.style // Override with user-provided styles via the logo component
  );
  var DefaultLogo = lang === LANG.EN_CA ? /*#__PURE__*/jsx(Manulife.WORDMARK_WHITE, {}) : /*#__PURE__*/jsx(ManulifeFr.WORDMARK_WHITE, {});
  var MobileLogo = mobileLogo || DefaultLogo;
  var WordmarkLogo = /*#__PURE__*/React.cloneElement(MobileLogo, {
    title: wordmarkLogoAlt || i18n[lang].sideNav.wordmarkLogo,
    style: logoStyle,
    'data-testid': 'wordmark-logo',
    'aria-hidden': !wordmarkLogoHref && !wordmarkLogoOnClick ? true : undefined
  }, null);
  function handleNavItemClick(key) {
    setActiveKey(key);
    if (showHamburgerMenu) {
      setExpandedParentKey();
      setMobileNavExpanded(false);
    }
  }
  var handleMobileNavToggle = useCallback(function (e) {
    toggleMobileNav();
    setExpandedParentKey();
    handle(onToggleMobileNav)(e, !isMobileNavExpanded);
  }, [isMobileNavExpanded, toggleMobileNav, onToggleMobileNav]);
  var context = useMemo(function () {
    return {
      isWideMainNav: isWideMainNav,
      keepOpenOnSelect: keepOpenOnSelect,
      onToggleSideNav: onToggleSideNav,
      brandLogoProps: brandLogoProps,
      wordmarkLogoProps: wordmarkLogoProps,
      logo: logo,
      activeKey: activeKey,
      activeParentKey: activeParentKey,
      expandedParentKey: expandedParentKey,
      handleNavItemClick: handleNavItemClick,
      setActiveParentKey: setActiveParentKey,
      setExpandedParentKey: setExpandedParentKey,
      setMobileNavExpanded: setMobileNavExpanded,
      isMobileNavExpanded: isMobileNavExpanded,
      mobileTabs: mobileTabs,
      onChangeMobileTabs: onChangeMobileTabs,
      mobileSelectedTab: mobileSelectedTab,
      mobileHeaderItems: mobileHeaderItems,
      mobileFooterItems: mobileFooterItems,
      selectedTab: selectedTab,
      setSelectedTab: setSelectedTab,
      subNavRef: subNavRef
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isWideMainNav, keepOpenOnSelect, onToggleSideNav, brandLogoProps, wordmarkLogoProps, logo, activeKey, activeParentKey, expandedParentKey, setMobileNavExpanded, isMobileNavExpanded, mobileTabs, onChangeMobileTabs, mobileSelectedTab, mobileHeaderItems, mobileFooterItems, selectedTab, subNavRef]);
  return /*#__PURE__*/jsx(Root, {
    id: id,
    "data-testid": "nav",
    ref: outsideClickTarget,
    isSticky: isSticky,
    theme: theme,
    children: /*#__PURE__*/jsx(SideNavContext.Provider, {
      value: context,
      children: showHamburgerMenu ? /*#__PURE__*/jsxs(Fragment, {
        children: [skipLinks, /*#__PURE__*/jsxs(FocusScope, {
          contain: isMobileNavExpanded,
          children: [theme.sideNav.brandLogo.showLogo && /*#__PURE__*/jsx(BrandLogo, _objectSpread({
            mobileProps: mobileProps
          }, brandLogoProps)), /*#__PURE__*/jsx(LogoComponent, {
            as: wordmarkLogoAs,
            type: "wordmark",
            href: wordmarkLogoHref,
            onClick: wordmarkLogoOnClick,
            children: WordmarkLogo
          }), /*#__PURE__*/jsxs(MobileNavButton, {
            "data-testid": "mobile-toggle-button",
            type: "button",
            isSelected: isMobileNavExpanded,
            onClick: handleMobileNavToggle,
            "aria-label": mobileProps.toggle.ariaLabel,
            "aria-expanded": isMobileNavExpanded,
            theme: theme,
            ref: mainNavRef,
            children: [mobileProps.toggle.icon, theme.sideNav.mobile.toggle.showLabel && /*#__PURE__*/jsx("div", {
              children: mobileProps.toggle.label
            })]
          }), /*#__PURE__*/jsx(MainNav, {
            children: children
          })]
        })]
      }) : /*#__PURE__*/jsx(MainNav, {
        isWideMainNav: isWideMainNav,
        mainNavRef: mainNavRef,
        children: children
      })
    })
  });
};
process.env.NODE_ENV !== "production" ? SideNav.propTypes = {
  /** id for the nav DOM element */
  id: PropTypes.string,
  /** itemKey of the current active item */
  activeKey: PropTypes.string,
  /** flag to change main nav width from 80px to 100px on desktops */
  isWideMainNav: PropTypes.bool,
  /**
   * flag to keep sub nav open after item is selected, and on reload (for desktops),
   * or to keep mobile nav open after tab selection (for mobile)
   * */
  keepOpenOnSelect: PropTypes.bool,
  /** flag to close sub nav on click outside */
  shouldCloseOnBlur: PropTypes.bool,
  /**
   * props to set a11y and control interaction with the brand logo for desktop and mobile
   */
  brandLogoProps: PropTypes.shape({
    as: PropTypes.node,
    alt: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func
  }),
  /**
   * props to set a11y and control interaction with the wordmark logo for desktop and mobile
   */
  wordmarkLogoProps: PropTypes.shape({
    as: PropTypes.node,
    alt: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func
  }),
  /**
   * Desktop [Logo](/docs/logos) component (optional).
   *
   * Recommended to use with `@manulife/mux-logos` library.
   * */
  logo: PropTypes.node,
  /**
   * Mobile [Logo](/docs/logos) component (optional).
   *
   * Recommended to use with `@manulife/mux-logos` library
   * */
  mobileLogo: PropTypes.node,
  /** SkipLinks component (rendered in mobile view only, use UtilityHeader for desktop view) */
  skipLinks: PropTypes.node,
  /** handler called when the side nav is opened/closed. Provides `isSideNavExpanded` */
  onToggleSideNav: PropTypes.func,
  /** handler called when mobile nav is opened/closed. Provides `isMobileNavExpanded` */
  onToggleMobileNav: PropTypes.func,
  /** tabs to show in the mobile nav */
  mobileTabs: PropTypes.arrayOf(PropTypes.shape({
    /** id for the DOM element of the tab */
    id: PropTypes.string.isRequired,
    /** label to be displayed on the tab */
    label: PropTypes.node.isRequired,
    /** value used to determine selected tab */
    value: PropTypes.string.isRequired
  })),
  /** value of selected tab; first tab is selected by default if this prop is not specified */
  mobileSelectedTab: PropTypes.string,
  /** onChange handler for mobile tabs */
  onChangeMobileTabs: PropTypes.func,
  /**
   * array of components to show in the mobile header section (e.g. Search, SignIn)
   *
   * Use this for components from `UtilityHeader` that you want to display in the mobile header section
   */
  mobileHeaderItems: PropTypes.array,
  /**
   * array of components to show in the mobile footer section (e.g. ContactUs, Lang)
   *
   * Use this for components from `UtilityHeader` that you want to display in the mobile footer section
   */
  mobileFooterItems: PropTypes.array,
  /** nav items provided within the SideNavSection component for each mobile tab */
  children: PropTypes.node,
  /** Ref of the first main nav item */
  mainNavRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),
  /** Ref of the first sub nav item */
  subNavRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })])
} : undefined;
process.env.NODE_ENV !== "production" ? SideNav.displayName = 'SideNav' : undefined;

export { SideNav as default };
